import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  moderators,
  recentActions,
  bannedUsers,
  modReports,
  moderatorRoleLabels,
  moderatorRoleIcons,
  moderatorRoleColors,
  modActionConfig,
  moderatorDashboardStats,
  moderatorRules,
  getModeratorsByRole,
  getOnlineModerators,
  type ModeratorRole,
  type ModAction,
} from '@/mocks/rfwModerators';

function ActionBadge({ action }: { action: ModAction }) {
  const config = modActionConfig[action];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${config.bgColor} ${config.color} border border-current/20`}>
      <i className={`${config.icon} text-[8px]`}></i>
      {config.label}
    </span>
  );
}

export default function ModeradoresSection() {
  const [activeTab, setActiveTab] = useState<'moderadores' | 'acciones' | 'dashboard'>('moderadores');
  const [roleFilter, setRoleFilter] = useState<ModeratorRole | 'todas'>('todas');
  const [actionFilter, setActionFilter] = useState<ModAction | 'todas'>('todas');

  const onlineMods = getOnlineModerators();
  const filteredMods = getModeratorsByRole(roleFilter);
  const filteredActions = actionFilter === 'todas'
    ? recentActions
    : recentActions.filter((a) => a.action === actionFilter);

  const tabs = [
    { id: 'moderadores' as const, label: 'Moderadores', icon: 'ri-shield-user-line', badge: moderators.length },
    { id: 'acciones' as const, label: 'Acciones Recientes', icon: 'ri-flashlight-line', badge: recentActions.length },
    { id: 'dashboard' as const, label: 'Dashboard', icon: 'ri-dashboard-line', badge: modReports.filter((r) => r.status === 'pendiente').length },
  ];

  const roleFilters: { id: ModeratorRole | 'todas'; label: string; icon: string }[] = [
    { id: 'todas', label: 'Todos', icon: 'ri-group-line' },
    { id: 'radio', label: 'Radio', icon: moderatorRoleIcons.radio },
    { id: 'programa', label: 'Programa', icon: moderatorRoleIcons.programa },
    { id: 'club', label: 'Club', icon: moderatorRoleIcons.club },
    { id: 'evento', label: 'Evento', icon: moderatorRoleIcons.evento },
  ];

  const actionFilters: { id: ModAction | 'todas'; label: string }[] = [
    { id: 'todas', label: 'Todas' },
    ...Object.entries(modActionConfig).map(([key, config]) => ({
      id: key as ModAction,
      label: config.label,
    })),
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
            SISTEMA DE MODERADORES
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Comunidades <span className="text-rose-600">Sanas</span>, Moderación Transparente
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Cuatro tipos de moderadores protegen cada rincón de RFW. Seis acciones con propósito. Cada decisión queda registrada para siempre. La confianza se construye con transparencia.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Moderadores Totales', value: (moderatorDashboardStats.totalModerators / 1000).toFixed(1) + 'K', icon: 'ri-shield-user-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'En Línea Ahora', value: moderatorDashboardStats.moderatorsOnline.toString(), icon: 'ri-user-smile-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Acciones Hoy', value: (moderatorDashboardStats.actionsToday / 1000).toFixed(1) + 'K', icon: 'ri-flashlight-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Bloqueos Activos', value: (moderatorDashboardStats.activeBlocks / 1000).toFixed(1) + 'K', icon: 'ri-forbid-line', color: 'text-red-600 bg-red-50' },
            { label: 'Tiempo Respuesta', value: moderatorDashboardStats.avgResponseTime, icon: 'ri-timer-line', color: 'text-sky-600 bg-sky-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-50 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex bg-background-50 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                {tab.badge && tab.badge > 0 && (
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-rose-100 text-rose-600'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'moderadores' && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {roleFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setRoleFilter(f.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    roleFilter === f.id
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  <i className={`${f.icon} text-[9px]`}></i>
                  {f.label}
                </button>
              ))}
            </div>

            {onlineMods.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  </div>
                  <p className="text-xs font-bold text-foreground-900">{onlineMods.length} moderadores en línea</p>
                </div>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {onlineMods.map((mod) => {
                    const roleColors = moderatorRoleColors[mod.role];
                    return (
                      <div key={mod.id} className="flex-shrink-0 flex flex-col items-center gap-1.5 p-2.5 rounded-xl bg-background-50 border border-background-200/70 hover:border-rose-300/60 transition-all duration-200 cursor-pointer">
                        <div className="relative">
                          <img src={mod.photoUrl} alt={mod.name} className="w-10 h-10 rounded-full object-cover" />
                          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-background-50"></span>
                        </div>
                        <p className="text-[10px] font-medium text-foreground-700 line-clamp-1 w-16 text-center">{mod.name.split(' ')[0]}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMods.slice(0, 6).map((mod) => {
                const roleColors = moderatorRoleColors[mod.role];
                return (
                  <Link
                    key={mod.id}
                    to="/moderadores"
                    className="p-4 rounded-2xl bg-background-50 border border-background-200/70 hover:border-rose-300/60 transition-all duration-200 group"
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="relative shrink-0">
                        <img src={mod.photoUrl} alt={mod.name} className="w-12 h-12 rounded-full object-cover" />
                        {mod.isOnline && (
                          <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-background-50"></span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <p className="text-sm font-bold text-foreground-950 line-clamp-1">{mod.name}</p>
                          {mod.isVerified && <i className="ri-verified-badge-fill text-[10px] text-sky-500"></i>}
                        </div>
                        <p className="text-[10px] text-foreground-500">{mod.username}</p>
                        <span className={`inline-block mt-1 text-[9px] px-1.5 py-0.5 rounded-full font-medium border ${roleColors.badge}`}>
                          {moderatorRoleLabels[mod.role]}
                        </span>
                      </div>
                    </div>
                    <p className="text-[10px] text-foreground-500 mb-2 line-clamp-1">
                      <i className="ri-map-pin-line text-[9px] mr-0.5"></i>{mod.assignedEntity}
                    </p>
                    <div className="flex items-center gap-3 text-[10px] text-foreground-500">
                      <span><i className="ri-flashlight-line text-[9px] mr-0.5"></i>{(mod.totalActions / 1000).toFixed(1)}K acciones</span>
                      <span><i className="ri-star-line text-[9px] mr-0.5"></i>Nivel {mod.level}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'acciones' && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {actionFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setActionFilter(f.id)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    actionFilter === f.id
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {filteredActions.slice(0, 6).map((act) => (
                <div key={act.id} className="flex items-start gap-3 p-3.5 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="relative shrink-0">
                    <img src={act.moderatorPhoto} alt={act.moderatorName} className="w-10 h-10 rounded-full object-cover" />
                    <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${modActionConfig[act.action].bgColor} flex items-center justify-center border-2 border-background-50`}>
                      <i className={`${modActionConfig[act.action].icon} text-[8px] ${modActionConfig[act.action].color}`}></i>
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <p className="text-xs font-bold text-foreground-950">{act.moderatorName}</p>
                      <ActionBadge action={act.action} />
                      <span className="text-[10px] text-foreground-400 ml-auto shrink-0">{act.timestampRelative}</span>
                    </div>
                    <p className="text-[11px] text-foreground-600 mb-1">
                      <span className="font-semibold">{act.targetType}</span> de <span className="font-medium">{act.targetUsername}</span>
                    </p>
                    <p className="text-[10px] text-foreground-500 line-clamp-1">{act.reason}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Reportes Recibidos', value: (moderatorDashboardStats.totalReportsReceived / 1000).toFixed(1) + 'K', sub: `${moderatorDashboardStats.reportsPending} pendientes`, icon: 'ri-alert-line', color: 'text-orange-600 bg-orange-50' },
                { label: 'Reportes Resueltos', value: (moderatorDashboardStats.reportsResolved / 1000).toFixed(1) + 'K', sub: '99.7% tasa resolución', icon: 'ri-check-double-line', color: 'text-emerald-600 bg-emerald-50' },
                { label: 'Bloqueos Activos', value: (moderatorDashboardStats.activeBlocks / 1000).toFixed(1) + 'K', sub: `${bannedUsers.length} recientes`, icon: 'ri-forbid-line', color: 'text-red-600 bg-red-50' },
                { label: 'Auditoría', value: (moderatorDashboardStats.totalAuditEntries / 1000000).toFixed(1) + 'M', sub: 'entradas registradas', icon: 'ri-file-search-line', color: 'text-slate-600 bg-slate-50' },
              ].map((stat, i) => (
                <div key={i} className="p-4 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                  <div className={`w-10 h-10 rounded-full ${stat.color} flex items-center justify-center mx-auto mb-2`}>
                    <i className={`${stat.icon} text-sm`}></i>
                  </div>
                  <p className="text-xl font-extrabold text-foreground-950">{stat.value}</p>
                  <p className="text-[11px] font-semibold text-foreground-700">{stat.label}</p>
                  <p className="text-[10px] text-foreground-400 mt-0.5">{stat.sub}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="p-4 rounded-2xl bg-background-50 border border-background-200/70">
                <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <i className="ri-shield-check-line text-rose-400"></i>
                  Reportes Recientes
                </h4>
                <div className="space-y-2">
                  {modReports.slice(0, 3).map((r) => (
                    <div key={r.id} className="flex items-center justify-between p-2.5 rounded-lg bg-background-100">
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold text-foreground-900 line-clamp-1">{r.reportedTargetUsername}</p>
                        <p className="text-[9px] text-foreground-500">{r.reason.slice(0, 60)}...</p>
                      </div>
                      <span className={`shrink-0 ml-2 text-[9px] px-2 py-0.5 rounded-full font-medium ${
                        r.status === 'pendiente' ? 'bg-amber-100 text-amber-700' :
                        r.status === 'resuelto' ? 'bg-emerald-100 text-emerald-700' :
                        r.status === 'revisado' ? 'bg-sky-100 text-sky-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        {r.statusLabel}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-4 rounded-2xl bg-background-50 border border-background-200/70">
                <h4 className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <i className="ri-scales-line text-amber-400"></i>
                  Últimos Bloqueos
                </h4>
                <div className="space-y-2">
                  {bannedUsers.slice(0, 3).map((b) => (
                    <div key={b.id} className="p-2.5 rounded-lg bg-background-100">
                      <div className="flex items-center justify-between mb-0.5">
                        <p className="text-[11px] font-semibold text-foreground-900">{b.username}</p>
                        <span className={`text-[9px] font-medium ${b.isPermanent ? 'text-red-600' : 'text-amber-600'}`}>
                          {b.duration}
                        </span>
                      </div>
                      <p className="text-[9px] text-foreground-500">por {b.bannedByUsername} · {b.reason.slice(0, 70)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {moderatorRules.map((rule) => (
            <div key={rule.id} className={`p-5 rounded-2xl ${rule.bgColor} border border-background-200/50 text-center`}>
              <div className={`w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-2`}>
                <i className={`${rule.icon} text-lg ${rule.color}`}></i>
              </div>
              <p className="text-sm font-bold text-foreground-950 mb-1">{rule.title}</p>
              <p className="text-[11px] text-foreground-500">{rule.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link
            to="/moderadores"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-shield-user-line"></i>
            Ver Sistema de Moderadores
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-rose-100/70 border border-rose-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-file-search-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Todo debe quedar auditado.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            Cada acción de moderación en RFW — ocultar, eliminar, silenciar, bloquear, reportar y destacar — queda registrada con fecha, hora, moderador responsable, motivo detallado y estado anterior y posterior del contenido o usuario afectado. La transparencia no es opcional: es la base sobre la que construimos la confianza de toda la comunidad radial.
          </p>
        </div>
      </div>
    </section>
  );
}